import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DataTable, { createTheme } from 'react-data-table-component';
import { FaEdit, FaEye, FaRegStar, FaStar } from 'react-icons/fa';
import { getAdminListingsByType, deleteListing, updateListingStatus } from '../../api';
import { assetUrl } from '../../utils';
import ListingModal from './ListingModal';

createTheme('millenniumDark', {
  text: { primary: '#ffffff', secondary: '#BDBDBD' },
  background: { default: 'transparent' },
  context: { background: '#EEC24B', text: '#111111' },
  divider: { default: '#262626' },
  button: { default: '#ffffff', hover: '#EEC24B', focus: '#EEC24B', disabled: '#666666' },
  sortFocus: '#EEC24B',
  selected: { default: '#1A1A1A', text: '#ffffff' },
  highlightOnHover: { default: '#141414', text: '#ffffff' },
});

const badgeClass = (value, kind) => {
  if (kind === 'status') {
    return value === 'published'
      ? 'bg-emerald-950/60 text-emerald-200 border-emerald-900/40'
      : 'bg-zinc-800 text-zinc-300 border-zinc-700';
  }

  return value
    ? 'bg-amber-950/60 text-amber-200 border-amber-900/40'
    : 'bg-zinc-800 text-zinc-300 border-zinc-700';
};

const ListingDetailRow = ({ data }) => {
  const summary = data.summary || data.description || 'No description provided.';
  const listingMeta = data.listingType === 'rent'
    ? [
        ['Building Size', data.rentDetails?.buildingSize],
        ['Property Type', data.rentDetails?.propertyType],
        ['Property Subtype', data.rentDetails?.propertySubtype],
        ['Year Built', data.rentDetails?.yearBuilt],
      ]
    : [
        ['MLS #', data.propertyDetails?.mlsNumber],
        ['MLS Type', data.propertyDetails?.mlsType],
        ['Bedrooms', data.propertyDetails?.bedrooms],
        ['Bathrooms', data.propertyDetails?.bathrooms],
      ];

  return (
    <div className="grid gap-[1rem] rounded-b-[18px] border-x border-b border-[#262626] bg-[#0f0f0f] p-[1rem] md:grid-cols-[1.2fr_0.8fr]">
      <div className="grid gap-[0.85rem]">
        <div className="rounded-[16px] border border-[#262626] bg-[#111] p-[1rem]">
          <p className="text-[12px] uppercase tracking-[0.14em] text-[#999]">Summary</p>
          <p className="mt-[0.45rem] text-[15px] leading-relaxed text-white">{summary}</p>
        </div>
        <div className="grid gap-[0.75rem] md:grid-cols-2">
          {listingMeta.map(([label, value]) =>
            value ? (
              <div key={label} className="rounded-[14px] border border-[#262626] bg-[#111] p-[0.9rem]">
                <p className="text-[11px] uppercase tracking-[0.12em] text-[#999]">{label}</p>
                <p className="mt-[0.25rem] text-[14px] font-semibold text-white">{value}</p>
              </div>
            ) : null
          )}
        </div>
      </div>
      <div className="grid gap-[0.75rem]">
        <div className="rounded-[16px] border border-[#262626] bg-[#111] p-[1rem]">
          <p className="text-[12px] uppercase tracking-[0.14em] text-[#999]">Links</p>
          <div className="mt-[0.65rem] space-y-[0.5rem] text-[14px] text-white">
            {data.externalUrl ? <a href={data.externalUrl} target="_blank" rel="noreferrer" className="block break-all text-[#EEC24B]">Source listing</a> : null}
            {data.sampleWebsite ? <a href={data.sampleWebsite} target="_blank" rel="noreferrer" className="block break-all text-[#EEC24B]">Sample website</a> : null}
            {data.mapUrl ? <a href={data.mapUrl} target="_blank" rel="noreferrer" className="block break-all text-[#EEC24B]">Map / location</a> : null}
          </div>
        </div>
        <div className="rounded-[16px] border border-[#262626] bg-[#111] p-[1rem]">
          <p className="text-[12px] uppercase tracking-[0.14em] text-[#999]">Admin Note</p>
          <p className="mt-[0.45rem] text-[14px] leading-relaxed text-white">{data.description || 'No admin note added.'}</p>
        </div>
      </div>
    </div>
  );
};

const AdminListingPage = ({ listingType, heading, subtitle }) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeListing, setActiveListing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [filters, setFilters] = useState({ q: '', status: 'all', featured: 'all', limit: 10 });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, limit: 10 });
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await getAdminListingsByType(listingType, {
          page: pagination.page,
          limit: filters.limit,
          q: filters.q || undefined,
          status: filters.status === 'all' ? undefined : filters.status,
          featured: filters.featured === 'all' ? undefined : filters.featured,
        });
        if (!active) return;
        setListings(data.listings || []);
        setPagination((prev) => ({
          ...prev,
          ...data.pagination,
          limit: filters.limit,
        }));
      } catch (err) {
        if (active) setError(err.response?.data?.message || 'Failed to load listings');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [listingType, pagination.page, filters.q, filters.status, filters.featured, filters.limit]);

  const refreshFirstPage = useCallback(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setResetPaginationToggle((prev) => !prev);
  }, []);

  const handleDelete = useCallback(
    async (id) => {
      if (!window.confirm('Delete this listing?')) return;
      try {
        await deleteListing(id);
        refreshFirstPage();
      } catch (err) {
        setError(err.response?.data?.message || 'Unable to delete listing');
      }
    },
    [refreshFirstPage]
  );

  const handleStatusToggle = useCallback(
    async (listing) => {
      const nextStatus = listing.status === 'published' ? 'draft' : 'published';
      try {
        await updateListingStatus(listing._id, nextStatus);
        refreshFirstPage();
      } catch (err) {
        setError(err.response?.data?.message || 'Unable to change status');
      }
    },
    [refreshFirstPage]
  );

  const onSaved = (listing) => {
    setListings((prev) => {
      const exists = prev.some((item) => item._id === listing._id);
      if (exists) {
        return prev.map((item) => (item._id === listing._id ? listing : item));
      }
      return [listing, ...prev];
    });
    setActiveListing(null);
    setShowCreate(false);
    refreshFirstPage();
  };

  const columns = useMemo(
    () => [
      {
        name: 'Listing',
        minWidth: '340px',
        cell: (row) => (
          <div className="flex items-center gap-[0.85rem] py-[0.4rem]">
            <img
              src={assetUrl(row.mainImage)}
              alt={row.title}
              className="h-[58px] w-[58px] rounded-[14px] border border-[#262626] object-cover"
            />
            <div className="min-w-0">
              <p className="truncate text-[15px] font-semibold text-white">{row.title}</p>
              <p className="truncate text-[13px] text-[#999]">{row.slug}</p>
            </div>
          </div>
        ),
      },
      {
        name: 'Type',
        width: '160px',
        cell: (row) => (
          <span className="rounded-full border border-[#262626] bg-[#111] px-[0.75rem] py-[0.35rem] text-[12px] font-semibold uppercase tracking-[0.12em] text-[#EEC24B]">
            {row.listingType === 'rent' ? 'Rent / Lease' : 'Property'}
          </span>
        ),
      },
      {
        name: 'Status',
        width: '140px',
        cell: (row) => (
          <span className={`rounded-full border px-[0.75rem] py-[0.35rem] text-[12px] font-semibold uppercase tracking-[0.12em] ${badgeClass(row.status, 'status')}`}>
            {row.status}
          </span>
        ),
      },
      {
        name: 'Featured',
        width: '120px',
        cell: (row) => (
          <span className={`inline-flex items-center gap-[0.4rem] rounded-full border px-[0.75rem] py-[0.35rem] text-[12px] font-semibold uppercase tracking-[0.12em] ${badgeClass(row.featured, 'featured')}`}>
            {row.featured ? <FaStar /> : <FaRegStar />}
            {row.featured ? 'Yes' : 'No'}
          </span>
        ),
      },
      {
        name: 'Updated',
        width: '150px',
        cell: (row) => (
          <span className="text-[13px] text-[#BDBDBD]">
            {row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : '-'}
          </span>
        ),
      },
      {
        name: 'Actions',
        minWidth: '260px',
        cell: (row) => (
          <div className="flex flex-wrap gap-[0.45rem] py-[0.4rem]">
            <button
              type="button"
              onClick={() => setActiveListing(row)}
              className="inline-flex items-center gap-[0.4rem] rounded-lg border border-[#262626] px-[0.8rem] py-[0.55rem] text-[13px] font-semibold text-white"
            >
              <FaEdit />
              Edit
            </button>
            <button
              type="button"
              onClick={() => handleStatusToggle(row)}
              className="rounded-lg border border-[#262626] px-[0.8rem] py-[0.55rem] text-[13px] font-semibold text-white"
            >
              {row.status === 'published' ? 'Draft' : 'Publish'}
            </button>
            <button
              type="button"
              onClick={() => handleDelete(row._id)}
              className="rounded-lg border border-red-900/40 px-[0.8rem] py-[0.55rem] text-[13px] font-semibold text-red-200"
            >
              Delete
            </button>
          </div>
        ),
      },
    ],
    [handleDelete, handleStatusToggle],
  );

  const subHeader = (
    <div className="grid w-full gap-[0.75rem] md:grid-cols-4">
      <label className="flex flex-col gap-[0.35rem] md:col-span-2">
        <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[#999]">Search</span>
        <input
          value={filters.q}
          onChange={(e) => {
            setFilters((prev) => ({ ...prev, q: e.target.value }));
            refreshFirstPage();
          }}
          placeholder="Search title, slug, summary, or description"
          className="rounded-lg border border-[#262626] bg-[#1A1A1A] px-[0.95rem] py-[0.8rem] text-white outline-none"
        />
      </label>
      <label className="flex flex-col gap-[0.35rem]">
        <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[#999]">Status</span>
        <select
          value={filters.status}
          onChange={(e) => {
            setFilters((prev) => ({ ...prev, status: e.target.value }));
            refreshFirstPage();
          }}
          className="rounded-lg border border-[#262626] bg-[#1A1A1A] px-[0.95rem] py-[0.8rem] text-white outline-none"
        >
          <option value="all">All</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
      </label>
      <label className="flex flex-col gap-[0.35rem]">
        <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[#999]">Featured</span>
        <select
          value={filters.featured}
          onChange={(e) => {
            setFilters((prev) => ({ ...prev, featured: e.target.value }));
            refreshFirstPage();
          }}
          className="rounded-lg border border-[#262626] bg-[#1A1A1A] px-[0.95rem] py-[0.8rem] text-white outline-none"
        >
          <option value="all">All</option>
          <option value="true">Featured</option>
          <option value="false">Not featured</option>
        </select>
      </label>
      <label className="flex flex-col gap-[0.35rem]">
        <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[#999]">Page size</span>
        <select
          value={filters.limit}
          onChange={(e) => {
            setFilters((prev) => ({ ...prev, limit: Number(e.target.value) }));
            refreshFirstPage();
          }}
          className="rounded-lg border border-[#262626] bg-[#1A1A1A] px-[0.95rem] py-[0.8rem] text-white outline-none"
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
        </select>
      </label>
    </div>
  );

  return (
    <div className="space-y-[1.25rem] px-[1.25rem] py-[1.25rem]">
      <div className="rounded-[18px] border border-[#262626] bg-[#111] p-[1.25rem]">
        <div className="flex flex-col gap-[1rem] lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-[#EEC24B]">{heading}</p>
            <h2 className="mt-[0.35rem] text-[28px] font-bold text-white">{subtitle}</h2>
          </div>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="rounded-lg bg-[#EEC24B] px-[1rem] py-[0.85rem] text-[15px] font-semibold text-black"
          >
            Add new {listingType === 'rent' ? 'rent listing' : 'property listing'}
          </button>
        </div>
        {error ? <p className="mt-[1rem] rounded-lg border border-red-900/40 bg-red-950/30 px-[1rem] py-[0.85rem] text-red-200">{error}</p> : null}
      </div>

      <div className="rounded-[18px] border border-[#262626] bg-[#111] p-[1rem]">
        <div className="mb-[0.75rem] flex items-center justify-between gap-[1rem]">
          <p className="text-[15px] text-[#BDBDBD]">
            Professional data table with server-side search, filters, and pagination.
          </p>
          <span className="rounded-full border border-[#262626] px-[0.8rem] py-[0.45rem] text-[13px] text-[#999]">
            {loading ? 'Loading...' : `${pagination.total} total`}
          </span>
        </div>

        <DataTable
          theme="millenniumDark"
          columns={columns}
          data={listings}
          progressPending={loading}
          progressComponent={<div className="py-[2rem] text-center text-[#BDBDBD]">Loading listings...</div>}
          noDataComponent={<div className="py-[2rem] text-center text-[#999]">No listings yet.</div>}
          highlightOnHover
          pointerOnHover
          responsive
          dense
          striped={false}
          fixedHeader
          fixedHeaderScrollHeight="68vh"
          pagination
          paginationServer
          paginationTotalRows={pagination.total}
          paginationPerPage={filters.limit}
          paginationRowsPerPageOptions={[5, 10, 20]}
          paginationDefaultPage={pagination.page}
          paginationResetDefaultPage={resetPaginationToggle}
          onChangePage={(page) => setPagination((prev) => ({ ...prev, page }))}
          onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
            setFilters((prev) => ({ ...prev, limit: currentRowsPerPage }));
            setPagination((prev) => ({ ...prev, page: currentPage }));
          }}
          expandableRows
          expandableRowsHideExpander={false}
          expandableRowsComponent={({ data }) => <ListingDetailRow data={data} />}
          expandOnRowClicked={false}
          selectableRowsHighlight={false}
          subHeader
          subHeaderComponent={subHeader}
          subHeaderAlign="left"
          actions={(
            <div className="flex items-center gap-[0.65rem]">
              <FaEye className="text-[#999]" />
              <span className="text-[13px] text-[#BDBDBD]">Row details expand below the table.</span>
            </div>
          )}
        />
      </div>

      <ListingModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        initialType={listingType}
        onSaved={onSaved}
      />
      <ListingModal
        open={Boolean(activeListing)}
        onClose={() => setActiveListing(null)}
        initialListing={activeListing}
        initialType={listingType}
        onSaved={onSaved}
      />
    </div>
  );
};

export default AdminListingPage;
